import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Radio, Typography } from 'antd';
import { CreateCharacterRequest } from '../api/models/Lifetree';

const { TextArea } = Input;
const { Text } = Typography;

interface CreateCharacterModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (data: CreateCharacterRequest) => Promise<void>;
  loading: boolean;
  defaultType?: 'main' | 'npc';
}

const CreateCharacterModal: React.FC<CreateCharacterModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  loading,
  defaultType
}) => {
  const [form] = Form.useForm();
  const [selectedCharacterType, setSelectedCharacterType] = useState<'main' | 'npc' | undefined>(defaultType);

  const handleCharacterTypeChange = (type: 'main' | 'npc') => {
    setSelectedCharacterType(type);
    if (type === 'main') {
      form.setFieldsValue({ name: '{{userName}}' });
    } else {
      form.setFieldsValue({ name: '' });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const createData: CreateCharacterRequest = {
        name: selectedCharacterType === 'main' ? '{{userName}}' : values.name,
        gender: values.gender,
        type: values.type,
        tags: values.tags ? values.tags.split(',').map((tag: string) => tag.trim()).filter((tag: string) => tag !== '') : [],
        description: values.description || '',
        displayedDescription: values.displayedDescription || '',
      };

      await onSubmit(createData);
      
      // Reset form
      form.resetFields();
      setSelectedCharacterType(defaultType);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedCharacterType(defaultType);
    onCancel();
  };

  // Reset form when visible changes
  useEffect(() => {
    if (visible && defaultType) {
      form.setFieldsValue({ type: defaultType });
      setSelectedCharacterType(defaultType);
    }
  }, [visible, defaultType, form]);

  return (
    <Modal
      title="Tạo nhân vật mới"
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      okText="Tạo nhân vật"
      cancelText="Hủy"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          label="Loại nhân vật"
          name="type"
          rules={[{ required: true, message: 'Vui lòng chọn loại nhân vật!' }]}
        >
          <Radio.Group onChange={(e) => handleCharacterTypeChange(e.target.value)}>
            <Radio value="main">Nhân vật chính</Radio>
            <Radio value="npc">NPC</Radio>
          </Radio.Group>
        </Form.Item>

        {selectedCharacterType === 'main' && (
          <div style={{ 
            marginBottom: 16, 
            padding: '8px 12px', 
            backgroundColor: '#fff7e6', 
            border: '1px solid #ffd591', 
            borderRadius: '6px' 
          }}>
            <Text type="warning">
              <strong>⚠️ Lưu ý:</strong> Tên nhân vật chính phải là {'{{userName}}'} để hệ thống tự động thay thế bằng tên người chơi
            </Text>
          </div>
        )}

        <Form.Item
          label="Tên nhân vật"
          name="name"
          rules={[
            { required: true, message: 'Vui lòng nhập tên nhân vật!' },
            { max: 100, message: 'Tên phải ít hơn 100 ký tự!' }
          ]}
        >
          <Input 
            placeholder={selectedCharacterType === 'main' ? '{{userName}}' : 'Nhập tên nhân vật'}
            disabled={selectedCharacterType === 'main'}
            value={selectedCharacterType === 'main' ? '{{userName}}' : undefined}
          />
        </Form.Item>

        <Form.Item
          label="Giới tính"
          name="gender"
          rules={[{ required: true, message: 'Vui lòng chọn giới tính!' }]}
        >
          <Radio.Group>
            <Radio value="male">Nam</Radio>
            <Radio value="female">Nữ</Radio>
            <Radio value="other">Khác</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Tags"
          name="tags"
          help="Nhập các tags cách nhau bằng dấu phẩy (VD: Thông minh, Bạn cùng phòng, Thiên tài)"
        >
          <Input placeholder="Thông minh, Bạn cùng phòng, Thiên tài" />
        </Form.Item>

        <Form.Item
          label="Mô tả chi tiết"
          name="description"
          rules={[
            { max: 1000, message: 'Mô tả phải ít hơn 1000 ký tự!' }
          ]}
        >
          <TextArea 
            rows={3} 
            placeholder="Mô tả chi tiết cho admin (tùy chọn)"
          />
        </Form.Item>

        <Form.Item
          label="Mô tả hiển thị"
          name="displayedDescription"
          rules={[
            { max: 255, message: 'Mô tả hiển thị phải ít hơn 255 ký tự!' }
          ]}
          help="Mô tả ngắn gọn hiển thị cho người chơi"
        >
          <Input placeholder="Mô tả ngắn gọn hiển thị cho người chơi (tùy chọn)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateCharacterModal; 